'use client';

import { useCallback, useMemo } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { type Control, useController, useForm } from 'react-hook-form';

import { ReactComponent as Canada } from '@quick/assets/countries/canada.svg';
import { ReactComponent as Chile } from '@quick/assets/countries/chile.svg';
import { ReactComponent as Colombia } from '@quick/assets/countries/colombia.svg';
import { ReactComponent as Ecuador } from '@quick/assets/countries/ecuador.svg';
import { ReactComponent as Mexico } from '@quick/assets/countries/mexico.svg';
import { ReactComponent as Peru } from '@quick/assets/countries/peru.svg';
import { ReactComponent as USA } from '@quick/assets/countries/usa.svg';
import { ReactComponent as Logo } from '@quick/assets/logos/quick.svg';
import { Button } from '@quick/components/Button';
import {
  FloatingLabel,
  Password,
  WrappedInput,
  WrappedSelect,
} from '@quick/components/Forms';
import { IconSelect } from '@quick/components/IconSelect';
import { useToast } from '@quick/components/Toaster';
import type { CountryType } from '@quick/go-api-types/services/countries';
import type { DocumentTypes } from '@quick/go-api-types/users/document_type';
import { useJSLoaded, useRecaptcha } from '@quick/utils';

import { useAppTranslation } from '@/app/i18n/client';
import { useLangProvider } from '@/components/core/AppLayout';
import { AutocompleteCities } from '@/components/core/AutoCompleteCities';
import { InlineSpinner } from '@/components/core/InlineSpinner';
import { Separator } from '@/components/core/Separator';
import { envs } from '@/layers/core/data/const';
import msgFromAxiosException from '@/layers/core/deal/mappers/msgFromAxiosException';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { usePasswordRequirements } from '@/layers/core/deal/services/usePasswordRequirements';
import { email, onlyNumbers } from '@/layers/core/helpers/regex';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

type UserForm = {
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone: string;
  nids_id: string;
  nid: string;
  city_id: string;
  password: string;
  WrappedSelect: string;
  nid_company: string;
  company_name: string;
  legal_name: string;
  address: string;
  contact_role: string;
  billing_address: string;
  validatePassword: string;
};

interface Created {
  status: 'active' | 'pending';
}

const RegisterUser = () => {
  const { id: country_id, code, bequicker } = useCountry();
  const lang = useLangProvider();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UserForm>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      mobile_phone: code.padStart(3, '0'),
      nids_id: '',
      nid: '',
      city_id: '',
      password: '',
      WrappedSelect: '1',
      nid_company: '',
      company_name: '',
      legal_name: '',
      address: '',
      contact_role: '',
      billing_address: '',
      validatePassword: '',
    },
  });
  const { t } = useAppTranslation(['account', 'common', 'dl']);

  const getKey = useRecaptcha(envs.grecaptchaApiKey);

  const { toast } = useToast();

  const selectValue = watch('WrappedSelect');
  const password = watch('password');
  const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (e) =>
    setValue('WrappedSelect', e.target.value);
  const onSubmit = handleSubmit(async ({ WrappedSelect, ...valid }) => {
    const config = {
      'Accept-Language': lang,
    };
    try {
      const googleToken = await getKey();
      const { data } =
        WrappedSelect === '1'
          ? await axios_demand.post<Created>(
              `/companies`,
              { ...valid, google_response_token: googleToken },
              {
                headers: config,
              }
            )
          : await axios_demand.post<Created>(
              `/users`,
              {
                first_name: valid.first_name,
                last_name: valid.last_name,
                email: valid.email,
                mobile_phone: valid.mobile_phone,
                nids_id: valid.nids_id,
                nid: valid.nid,
                city_id: valid.city_id,
                password: valid.password,
                address: valid.address,
                google_response_token: googleToken,
              },
              { headers: config }
            );
      toast({
        title: t('account:registerUser.alerts.createUser'),
        variant: 'success',
        duration: 5000,
      });
      if (data.status === 'active') {
        router.push(`/${lang}/account/validate?a=1`);
      } else {
        router.push(`/${lang}/account/validate`);
      }
    } catch (err) {
      const msg = msgFromAxiosException(err).msg;
      toast({
        title: `${t('account:registerUser.alerts.createUserError')}\n${msg}`,
        variant: 'danger',
        duration: 10000,
      });
    }
  });

  const { data: documentTypes } = useFetch<DocumentTypes[]>({
    url: `/users/document_type`,
    params: { active: true, pagination: false, country_or_id: country_id },
  });

  const passwordRequirements = usePasswordRequirements();

  const loadedJS = useJSLoaded();

  return (
    <div className="flex h-full flex-col items-center gap-y-4 p-2 !pt-8">
      <div className="flex flex-col gap-y-8 text-center lg:w-1/3">
        <div className="flex flex-row items-center justify-center gap-x-4">
          <Logo className="h-16" />
          <h1 className="font-normal">{t('account:registerUser.title')}</h1>
        </div>
        <Separator />
        <h2 className="font-extrabold">{t('dl:header.register')}</h2>
      </div>
      <form
        className="grid gap-x-4 gap-y-6 p-4 sm:grid-cols-2"
        onSubmit={onSubmit}
      >
        <WrappedSelect
          labelSize="md"
          label={t('account:registerUser.form.clientType')}
          value={selectValue}
          onChange={handleChange}
          id="clientType"
          className="sm:col-span-full"
        >
          <option value="">
            {t('account:registerUser.form.clientOptionOne')}
          </option>
          <option value="1">
            {t('account:registerUser.form.clientOptionTwo')}
          </option>
        </WrappedSelect>
        <WrappedInput
          labelSize="md"
          {...register('first_name', {
            required: t('common:msg.required_field'),
          })}
          label={t('account:registerUser.form.firstName')}
          id="account:registerUser.form.firstName"
          error={errors.first_name?.message}
          type="text"
        />
        <WrappedInput
          labelSize="md"
          {...register('last_name', {
            required: t('common:msg.required_field'),
          })}
          label={t('account:registerUser.form.lastName')}
          id="account:registerUser.form.lastName"
          error={errors.last_name?.message}
          type="text"
        />
        <WrappedSelect
          labelSize="md"
          {...register('nids_id', {
            required: t('common:msg.required_field'),
          })}
          id="account:registerUser.form.nidID"
          label={t('account:registerUser.form.nidID')}
          error={errors.nids_id?.message}
        >
          <option value="" hidden>
            {t('common:select_option')}
          </option>
          {documentTypes?.map(({ id, extended_name }) => (
            <option key={id} value={id}>
              {extended_name}
            </option>
          ))}
        </WrappedSelect>
        {selectValue === '1' ? (
          <WrappedInput
            labelSize="md"
            {...register('nid_company', {
              required: t('common:msg.required_field'),
              pattern: {
                value: onlyNumbers,
                message: t('common:msg.only_numbers'),
              },
            })}
            label={t('account:registerUser.form.nidCompany')}
            innerClassName="no-arrows"
            error={errors.nid_company?.message}
          />
        ) : (
          <WrappedInput
            labelSize="md"
            {...register('nid', {
              required: t('common:msg.required_field'),
              pattern: {
                value: onlyNumbers,
                message: t('common:msg.only_numbers'),
              },
            })}
            label={t('account:registerUser.form.idNumber')}
            id="nid"
            error={errors.nid?.message}
          />
        )}
        <PhoneInput control={control} />
        <label className="relative z-[15]">
          <FloatingLabel as="span" label={t('common:city')} size="md" />
          <AutocompleteCities
            control={control}
            nameFilter="city_id"
            rules={{ required: t('common:msg.required_field') }}
            params={{ country_id }}
            className="h-[42px] w-full [&_.rs\\_\\_control]:h-full"
          />
        </label>
        <WrappedInput
          labelSize="md"
          {...register('address', {
            required: t('common:msg.required_field'),
          })}
          label={t('account:registerUser.form.address')}
          error={errors.address?.message}
        />
        <WrappedInput
          labelSize="md"
          {...register('email', {
            required: t('common:msg.required_field'),
            pattern: {
              value: email,
              message: t('common:msg.invalid_email'),
            },
          })}
          id="account:registerUser.form.email"
          label={t('account:registerUser.form.email')}
          error={errors.email?.message}
        />
        <Password
          labelSize="md"
          {...register('password', {
            required: t('common:msg.required_field'),
            validate(val) {
              for (const requirement of passwordRequirements) {
                const meets =
                  typeof requirement.req === 'function'
                    ? requirement.req(val)
                    : requirement.req.test(val);
                if (!meets) return requirement.label;
              }
            },
          })}
          requirements={passwordRequirements}
          label={t('account:registerUser.form.password')}
          id="account:registerUser.form.password"
          error={errors.password?.message}
        />
        <Password
          labelSize="md"
          {...register('validatePassword', {
            required: t('common:msg.required_field'),
            validate: (val) => {
              if (val !== password) {
                return t('common:msg.matchPassword');
              }
            },
          })}
          label={t('account:registerUser.form.validatePassword')}
          id="validatePassword"
          error={errors.validatePassword?.message}
        />
        {selectValue === '1' && (
          <>
            <WrappedInput
              labelSize="md"
              {...register('company_name', {
                required: t('common:msg.required_field'),
              })}
              label={t('account:registerUser.form.companyName')}
              error={errors.company_name?.message}
            />
            <WrappedInput
              labelSize="md"
              {...register('legal_name', {
                required: t('common:msg.required_field'),
              })}
              label={t('account:registerUser.form.legalName')}
              error={errors.legal_name?.message}
            />
            <WrappedInput
              labelSize="md"
              {...register('contact_role', {
                required: t('common:msg.required_field'),
              })}
              label={t('account:registerUser.form.contactRole')}
              type="number"
              error={errors.contact_role?.message}
              innerClassName="no-arrows"
            />
            <WrappedInput
              labelSize="md"
              {...register('billing_address', {
                required: t('common:msg.required_field'),
              })}
              label={t('account:registerUser.form.billingAddress')}
              error={errors.billing_address?.message}
            />
          </>
        )}
        <div className="flex w-full flex-col items-center gap-4 pt-4 sm:col-span-2">
          <p className="text-center">
            {t('account:registerUser.loginQuestion')}{' '}
            <Link
              href={`/${lang}/login`}
              className="dark:text-brand dark:hover:text-brand-focus dark:focus:text-brand-focus
                font-bold hover:text-gray-700 focus:text-gray-700"
            >
              {t('dl:login.title')}
            </Link>
          </p>
          {!isSubmitting ? (
            <Button
              variant="approve"
              type="submit"
              className="w-full rounded-full sm:!w-1/3"
              disabled={!loadedJS || isSubmitting}
            >
              {t('registerUser.form.register')}
            </Button>
          ) : (
            <InlineSpinner />
          )}
          {bequicker && (
            <a
              href={bequicker}
              target="_blank"
              rel="noreferrer noopener"
              className="dark:text-brand dark:hover:text-brand-focus dark:focus:text-brand-focus
                text-center font-bold hover:text-gray-700 focus:text-gray-700"
            >
              {t('account:registerUser.beQuicker')}
            </a>
          )}
        </div>
      </form>
    </div>
  );
};

const countriesIcon = {
  CO: Colombia,
  CL: Chile,
  MX: Mexico,
  US: USA,
  PE: Peru,
  EC: Ecuador,
  CAN: Canada,
};

function PhoneInput({ control }: { control: Control<UserForm> }) {
  const { t } = useAppTranslation(['common', 'account']);
  const { data: countries } = useFetch<CountryType[]>({
    url: '/services/countries',
  });
  const { field, fieldState } = useController({
    control,
    name: 'mobile_phone',
    rules: {
      required: t('common:msg.required_field'),
    },
  });
  const countryCode = field.value.slice(0, 3);
  const phoneNumber = field.value.slice(3);

  const selectData = useMemo(
    () =>
      countries?.map((c) => ({
        text: `+${c.code.toString().padStart(2, '0')}`,
        Icon:
          countriesIcon[c.abbr.toUpperCase() as keyof typeof countriesIcon] ??
          Colombia,
        value: c.code.toString().padStart(3, '0'),
      })) ?? [],
    [countries]
  );
  const onChange = field.onChange;
  const handleSelectChange = useCallback(
    ({ value }: { value: string }) => onChange(`${value}${phoneNumber}`),
    [onChange, phoneNumber]
  );
  return (
    <WrappedInput
      labelSize="md"
      className="z-20"
      {...field}
      value={phoneNumber}
      onChange={({ target }) => field.onChange(`${countryCode}${target.value}`)}
      type="number"
      innerClassName="no-arrows pl-32"
      placeholder="000-000-0000"
      error={fieldState.error?.message}
      label={t('account:registerUser.form.mobilePhone')}
      id="account:registerUser.form.mobilePhone"
    >
      {countries && (
        <IconSelect
          key={countryCode}
          defaultValue={countryCode}
          data={selectData}
          longPlaceholder={t('common:country')}
          smallPlaceholder={t('common:country')}
          className="z-2 absolute bottom-1/2 left-0.5 translate-y-1/2 outline-none"
          onChange={handleSelectChange}
          windowWide={340}
          selectStyles={{
            control: (prov) => ({
              ...prov,
              borderBlockWidth: 0,
              borderLeftWidth: 0,
              borderRadius: 0,
            }),
          }}
        />
      )}
    </WrappedInput>
  );
}

export default RegisterUser;
